"use client";

import { Section } from "@astryxdesign/core/Section";
import { Center } from "@astryxdesign/core/Center";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/VStack";
import { COMPANY_ADDRESS, COMPANY_GEO, COMPANY_YANDEX_MAP_URL } from "@/lib/brand/organization";
import { YandexMapEmbed } from "@/components/geo/yandex-map-embed";
import { DEFAULT_YANDEX_MAP } from "@/lib/landing/puck/contacts-defaults";
import { LANDING_SECTION_MAX } from "@/lib/landing/layout";
import {
  normalizeBackgroundColor,
  styledTextString,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";

export type LandingYandexMapLayout = "hero" | "section";
export type LandingYandexMapWidth = "full" | "container";

export type LandingYandexMapViewProps = {
  sectionId?: string;
  backgroundColor?: string;
  lat?: number;
  lon?: number;
  zoom?: number;
  heightPx?: number;
  addressLabel?: LandingTextValue;
  layout?: LandingYandexMapLayout;
  widthMode?: LandingYandexMapWidth;
  showAddress?: boolean;
};

function resolveNumber(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Виджет Яндекс.Карт с меткой офиса. */
export function LandingYandexMapView({
  sectionId = DEFAULT_YANDEX_MAP.sectionId,
  backgroundColor,
  lat = COMPANY_GEO.lat,
  lon = COMPANY_GEO.lon,
  zoom = DEFAULT_YANDEX_MAP.zoom,
  heightPx = DEFAULT_YANDEX_MAP.heightPx,
  addressLabel = DEFAULT_YANDEX_MAP.addressLabel,
  layout = DEFAULT_YANDEX_MAP.layout,
  widthMode = DEFAULT_YANDEX_MAP.widthMode,
  showAddress = DEFAULT_YANDEX_MAP.showAddress,
}: LandingYandexMapViewProps = {}) {
  const color = normalizeBackgroundColor(backgroundColor);
  const latN = resolveNumber(lat, COMPANY_GEO.lat);
  const lonN = resolveNumber(lon, COMPANY_GEO.lon);
  const zoomN = resolveNumber(zoom, DEFAULT_YANDEX_MAP.zoom);
  const height = Math.min(Math.max(resolveNumber(heightPx, DEFAULT_YANDEX_MAP.heightPx), 240), 720);
  const isHero = layout === "hero";
  const isFull = widthMode === "full" || isHero;
  const address =
    styledTextString(addressLabel).trim() || COMPANY_ADDRESS.formatted;
  const showAddr =
    showAddress === true || showAddress === ("true" as unknown as boolean);
  const browseHref = COMPANY_YANDEX_MAP_URL;

  const frame = (
    <YandexMapEmbed
      lat={latN}
      lon={lonN}
      zoom={zoomN}
      height={isHero ? "100%" : height}
      title={`Карта: ${address}`}
      className={isHero ? "h-full w-full" : "w-full"}
      iframeClassName={
        isHero
          ? "block h-full w-full border-0"
          : "block h-full w-full rounded-xl border border-border"
      }
    />
  );

  if (isHero) {
    return (
      <section
        id={sectionId || undefined}
        className="w-full"
        style={color ? { backgroundColor: color } : undefined}
      >
        <div className="w-full overflow-hidden" style={{ height }}>
          {frame}
        </div>
        {showAddr ? (
          <Center>
            <div
              className="w-full px-4 py-3 sm:px-6"
              style={{ maxWidth: LANDING_SECTION_MAX }}
            >
              <a
                href={browseHref}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-secondary underline-offset-2 hover:text-primary hover:underline"
              >
                {address}
              </a>
            </div>
          </Center>
        ) : null}
      </section>
    );
  }

  return (
    <Section
      id={sectionId || undefined}
      variant="transparent"
      paddingBlock={6}
      style={color ? { backgroundColor: color } : undefined}
    >
      <Center>
        <VStack
          width="100%"
          gap={2}
          align="stretch"
          className="min-w-0 px-4 sm:px-6"
          style={{ maxWidth: isFull ? LANDING_SECTION_MAX : LANDING_SECTION_MAX }}
        >
          <a
            href={browseHref}
            target="_blank"
            rel="noreferrer"
            className="block w-full overflow-hidden rounded-xl border border-border bg-muted"
            style={{ height }}
          >
            <YandexMapEmbed
              lat={latN}
              lon={lonN}
              zoom={zoomN}
              height="100%"
              title={`Карта: ${address}`}
              className="h-full w-full"
              iframeClassName="h-full w-full border-0"
            />
          </a>
          {showAddr ? (
            <Text type="supporting" color="secondary" className="text-sm">
              <a
                href={browseHref}
                target="_blank"
                rel="noreferrer"
                className="underline-offset-2 hover:underline"
              >
                {address}
              </a>
              {" · "}
              Яндекс.Карты
            </Text>
          ) : null}
        </VStack>
      </Center>
    </Section>
  );
}
